import { localizeWithPath } from '../../../../nls.js';
import { EditorContextKeys } from '../../../common/editorContextKeys.js';
import { HoverController } from './hoverController.js';
import { AccessibleViewType, AccessibleViewProviderId } from '../../../../platform/accessibility/browser/accessibleView.js';
import { IContextViewService } from '../../../../platform/contextview/browser/contextView.service.js';
import { IHoverService } from '../../../../platform/hover/browser/hover.service.js';
import { IInstantiationService } from '../../../../platform/instantiation/common/instantiation.js';
import { HoverVerbosityAction } from '../../../common/languages.js';
import { INCREASE_HOVER_VERBOSITY_ACTION_ID, DECREASE_HOVER_VERBOSITY_ACTION_ID, DECREASE_HOVER_VERBOSITY_ACCESSIBLE_ACTION_ID, INCREASE_HOVER_VERBOSITY_ACCESSIBLE_ACTION_ID } from './hoverActionIds.js';
import { ICodeEditorService } from '../../../browser/services/codeEditorService.js';
import { Action } from '../../../../base/common/actions.js';
import { ThemeIcon } from '../../../../base/common/themables.js';
import { Codicon } from '../../../../base/common/codicons.js';
import { Emitter } from '../../../../base/common/event.js';
import { Disposable } from '../../../../base/common/lifecycle.js';
import { IKeybindingService } from '../../../../platform/keybinding/common/keybinding.service.js';
import { labelForHoverVerbosityAction } from './markdownHoverParticipant.js';
const _moduleId = "vs/editor/contrib/hover/browser/hoverAccessibleViews";
var HoverAccessibilityHelpNLS;
( ((function(HoverAccessibilityHelpNLS) {
    HoverAccessibilityHelpNLS.intro = ( localizeWithPath(
        _moduleId,
        0,
        "Focus on the hover widget to cycle through the hover parts with the Tab key."
    ));
    HoverAccessibilityHelpNLS.increaseVerbosity = ( localizeWithPath(
        _moduleId,
        1,
        "- The focused hover part verbosity level can be increased with the Increase Hover Verbosity command<keybinding:{0}>.",
        INCREASE_HOVER_VERBOSITY_ACTION_ID
    ));
    HoverAccessibilityHelpNLS.decreaseVerbosity = ( localizeWithPath(
        _moduleId,
        2,
        "- The focused hover part verbosity level can be decreased with the Decrease Hover Verbosity command<keybinding:{0}>.",
        DECREASE_HOVER_VERBOSITY_ACTION_ID
    ));
    HoverAccessibilityHelpNLS.hoverContent = ( localizeWithPath(_moduleId, 3, "The last focused hover content is the following."));
})(HoverAccessibilityHelpNLS || (HoverAccessibilityHelpNLS = {}))));
class HoverAccessibleView {
    constructor() {
        this.type = AccessibleViewType.View;
        this.priority = 95;
        this.name = 'hover';
        this.when = EditorContextKeys.hoverFocused;
    }
    getProvider(accessor) {
        const codeEditorService = accessor.get(ICodeEditorService);
        const codeEditor = codeEditorService.getActiveCodeEditor() || codeEditorService.getFocusedCodeEditor();
        if (!codeEditor) {
            throw ( (new Error('No active or focused code editor')));
        }
        const hoverController = HoverController.get(codeEditor);
        if (!hoverController) {
            return;
        }
        const keybindingService = accessor.get(IKeybindingService);
        this._provider = accessor.get(IInstantiationService).createInstance(HoverAccessibleViewProvider, keybindingService, codeEditor, hoverController);
        return this._provider;
    }
    dispose() {
        this._provider?.dispose();
    }
}
class HoverAccessibilityHelp {
    constructor() {
        this.priority = 100;
        this.name = 'hover';
        this.type = AccessibleViewType.Help;
        this.when = EditorContextKeys.hoverVisible;
    }
    getProvider(accessor) {
        const codeEditorService = accessor.get(ICodeEditorService);
        const codeEditor = codeEditorService.getActiveCodeEditor() || codeEditorService.getFocusedCodeEditor();
        if (!codeEditor) {
            throw ( (new Error('No active or focused code editor')));
        }
        const hoverController = HoverController.get(codeEditor);
        if (!hoverController) {
            return;
        }
        return accessor.get(IInstantiationService).createInstance(HoverAccessibilityHelpProvider, hoverController);
    }
    dispose() {
        this._provider?.dispose();
    }
}
class BaseHoverAccessibleViewProvider extends Disposable {
    constructor(_hoverController) {
        super();
        this._hoverController = _hoverController;
        this.id = AccessibleViewProviderId.Hover;
        this.verbositySettingKey = 'accessibility.verbosity.hover';
        this._onDidChangeContent = this._register(( (new Emitter())));
        this.onDidChangeContent = this._onDidChangeContent.event;
        this._markdownHoverFocusedIndex = -1;
    }
    onOpen() {
        if (!this._hoverController) {
            return;
        }
        this._hoverController.shouldKeepOpenOnEditorMouseMoveOrLeave = true;
        this._markdownHoverFocusedIndex = this._hoverController.focusedMarkdownHoverIndex();
        this._onHoverContentsChanged = this._register(this._hoverController.onHoverContentsChanged(() => {
            this._onDidChangeContent.fire();
        }));
    }
    onClose() {
        if (!this._hoverController) {
            return;
        }
        this._markdownHoverFocusedIndex = -1;
        this._hoverController.focus();
        this._hoverController.shouldKeepOpenOnEditorMouseMoveOrLeave = false;
        this._onHoverContentsChanged?.dispose();
    }
}
class HoverAccessibilityHelpProvider extends BaseHoverAccessibleViewProvider {
    constructor(hoverController) {
        super(hoverController);
        this.options = { type: AccessibleViewType.Help };
    }
    provideContent() {
        return this.provideContentAtIndex(this._markdownHoverFocusedIndex);
    }
    provideContentAtIndex(index) {
        const content = [];
        content.push(HoverAccessibilityHelpNLS.intro);
        content.push(...this._descriptionsOfVerbosityActionsForIndex(index));
        content.push(...this._descriptionOfFocusedMarkdownHoverAtIndex(index));
        return content.join('\n');
    }
    _descriptionsOfVerbosityActionsForIndex(index) {
        const content = [];
        const descriptionForIncreaseAction = this._descriptionOfVerbosityActionForIndex(HoverVerbosityAction.Increase, index);
        if (descriptionForIncreaseAction !== undefined) {
            content.push(descriptionForIncreaseAction);
        }
        const descriptionForDecreaseAction = this._descriptionOfVerbosityActionForIndex(HoverVerbosityAction.Decrease, index);
        if (descriptionForDecreaseAction !== undefined) {
            content.push(descriptionForDecreaseAction);
        }
        return content;
    }
    _descriptionOfVerbosityActionForIndex(action, index) {
        const isActionSupported = this._hoverController.doesMarkdownHoverAtIndexSupportVerbosityAction(index, action);
        if (!isActionSupported) {
            return;
        }
        switch (action) {
            case HoverVerbosityAction.Increase:
                return HoverAccessibilityHelpNLS.increaseVerbosity;
            case HoverVerbosityAction.Decrease:
                return HoverAccessibilityHelpNLS.decreaseVerbosity;
        }
    }
    _descriptionOfFocusedMarkdownHoverAtIndex(index) {
        const content = [];
        const hoverContent = this._hoverController.markdownHoverContentAtIndex(index);
        if (hoverContent) {
            content.push('\n' + HoverAccessibilityHelpNLS.hoverContent);
            content.push('\n' + hoverContent);
        }
        return content;
    }
}
class HoverAccessibleViewProvider extends BaseHoverAccessibleViewProvider {
    constructor(_keybindingService, _editor, hoverController) {
        super(hoverController);
        this._keybindingService = _keybindingService;
        this._editor = _editor;
        this.options = { type: AccessibleViewType.View };
        this._initializeOptions(this._editor, hoverController);
    }
    provideContent() {
        const hoverContent = this._hoverController.markdownHoverContentAtIndex(this._markdownHoverFocusedIndex);
        return hoverContent.length > 0 ? hoverContent : this._hoverController.getWidgetContent() || HoverAccessibilityHelpNLS.intro;
    }
    get actions() {
        const actions = [];
        actions.push(this._getActionFor(this._editor, HoverVerbosityAction.Increase));
        actions.push(this._getActionFor(this._editor, HoverVerbosityAction.Decrease));
        return actions;
    }
    _getActionFor(editor, action) {
        let actionId;
        let accessibleActionId;
        let actionCodicon;
        switch (action) {
            case HoverVerbosityAction.Increase:
                actionId = INCREASE_HOVER_VERBOSITY_ACTION_ID;
                accessibleActionId = INCREASE_HOVER_VERBOSITY_ACCESSIBLE_ACTION_ID;
                actionCodicon = Codicon.add;
                break;
            case HoverVerbosityAction.Decrease:
                actionId = DECREASE_HOVER_VERBOSITY_ACTION_ID;
                accessibleActionId = DECREASE_HOVER_VERBOSITY_ACCESSIBLE_ACTION_ID;
                actionCodicon = Codicon.remove;
                break;
        }
        const actionLabel = labelForHoverVerbosityAction(this._keybindingService, action);
        const actionEnabled = this._hoverController.doesMarkdownHoverAtIndexSupportVerbosityAction(this._markdownHoverFocusedIndex, action);
        return (
             (new Action(
                accessibleActionId,
                actionLabel,
                ThemeIcon.asClassName(actionCodicon),
                actionEnabled,
                () => {
                    editor.getAction(actionId)?.run({ index: this._markdownHoverFocusedIndex, focus: false });
                }
            ))
        );
    }
    _initializeOptions(editor, hoverController) {
        const helpProvider = this._register(( (new HoverAccessibilityHelpProvider(hoverController))));
        this.options.language = editor.getModel()?.getLanguageId();
        this.options.customHelp = () => { return helpProvider.provideContentAtIndex(this._markdownHoverFocusedIndex); };
    }
}
class ExtHoverAccessibleView {
    constructor() {
        this.type = AccessibleViewType.View;
        this.priority = 90;
        this.name = 'extension-hover';
    }
    getProvider(accessor) {
        const contextViewService = accessor.get(IContextViewService);
        const contextViewElement = contextViewService.getContextViewElement();
        const extensionHoverContent = contextViewElement?.textContent ?? undefined;
        const hoverService = accessor.get(IHoverService);
        if (contextViewElement.classList.contains('accessible-view-container') || !extensionHoverContent) {
            return;
        }
        return {
            id: AccessibleViewProviderId.Hover,
            verbositySettingKey: 'accessibility.verbosity.hover',
            provideContent() { return extensionHoverContent; },
            onClose() {
                hoverService.showAndFocusLastHover();
            },
            options: { language: 'typescript', type: AccessibleViewType.View }
        };
    }
    dispose() { }
}
export { ExtHoverAccessibleView, HoverAccessibilityHelp, HoverAccessibilityHelpProvider, HoverAccessibleView, HoverAccessibleViewProvider };
